import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useGetCustomerMeQuery } from "../../redux/services/customer";

import getSubdomain from "../../utils/getSubdomain";
import {
  getSubscription,
  requestPermission,
  subscribe,
} from "../../utils/notification";
import {
  fetchRepeatSuggestion,
  fetchCustomerNotifications,
  fetchDiningOptions,
  fetchStationOptions,
  patchCustomerAllergies,
  createOrder as apiCreateOrder,
} from "./chatApi";
import {
  EMPTY_STATE,
  EMPTY_RUNTIME,
  applyCustomerPrefill,
  applyChipAction,
  applyTextInput,
  pickNextStep,
  getStepView,
  labelForChip,
  validateTextInput,
  buildOrderPayload,
  classifyRepeatItems,
} from "./chatSteps";

let _msgCounter = 1;
const makeId = () => `m${Date.now()}-${_msgCounter++}`;
const botMsg = (text) => ({ id: makeId(), from: "bot", text });
const userMsg = (text) => ({ id: makeId(), from: "user", text });

// Voice layer — pluggable so an alternate-branded bot (e.g. Forkly) can supply
// its own phrasing while reusing all flow logic. Defaults reproduce the original
// AI-waiter copy exactly.
export const DEFAULT_VOICE = {
  getStepView,
  labelForChip,
  phrases: {
    placingOrder: () => "Placing your order now...",
    orderPlacedWithId: (id) => `You're all set! Order #${id} is on its way 🎉`,
    orderPlaced: () => "You're all set! Your order is on its way 🎉",
    orderFailedSuffix: () => "No worries, you can try again or start fresh.",
    bootstrapError: () => "Oops, something went wrong starting the chat. Try again?",
    pushFailRetry: (reason) => `${reason} Want to pick a different way to get notified?`,
    doneMultiLabel: (count) => (count ? `Done (${count})` : "Done"),
    noAllergiesLabel: () => "I have no allergies",
  },
};

const useChatBot = (isOpen, sendViaWS, voice = DEFAULT_VOICE) => {
  const getStepView = voice.getStepView;
  const labelForChip = voice.labelForChip;
  const phrases = voice.phrases;
  const openedStations = useSelector((s) => s.stations.openedStations) || [];
  const vapidKey = useSelector((s) => s.app.vapidKey);
  const { data: customer } = useGetCustomerMeQuery(undefined, {
    skip: !localStorage.getItem("access_token"),
  });

  // React state (UI-facing)
  const [bootstrapped, setBootstrapped] = useState(false);
  const [messages, setMessages] = useState([]);
  const [orderState, setOrderState] = useState(EMPTY_STATE);
  const [runtime, setRuntime] = useState(EMPTY_RUNTIME);
  const [stepId, setStepId] = useState("init");
  const [contextData, setContextData] = useState({
    suggestion: null,
    diningOptions: null,
    stationOptions: null,
  });
  const [notificationOptions, setNotificationOptions] = useState([]);
  const [selectedChips, setSelectedChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [inputError, setInputError] = useState(null);

  // Refs to always-fresh values for async callbacks
  const stationsRef = useRef(openedStations);
  const vapidKeyRef = useRef(vapidKey);
  const notificationsRef = useRef(notificationOptions);
  const customerRef = useRef(null);
  useEffect(() => { stationsRef.current = openedStations; }, [openedStations]);
  useEffect(() => { vapidKeyRef.current = vapidKey; }, [vapidKey]);
  useEffect(() => { notificationsRef.current = notificationOptions; }, [notificationOptions]);
  useEffect(() => { customerRef.current = customer ?? null; }, [customer]);

  const buildCtx = useCallback(
    (override) => ({
      stations: stationsRef.current,
      notificationOptions: notificationsRef.current,
      ...contextData,
      customer: customerRef.current || null,
      ...(override || {}),
    }),
    [contextData],
  );

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const TYPE_CHARS_PER_TICK = 3;
  const TYPE_TICK_MS = 18;
  const PRE_TYPE_MS = 280;
  const BETWEEN_MS = 200;

  const pushBotMessages = async (texts, opts = {}) => {
    const { highlight = false } = opts;
    const arr = Array.isArray(texts) ? texts : [texts];
    const toShow = arr
      .map((t) => (t && typeof t === "object" ? t : { text: t }))
      .filter(
        (t) =>
          (Array.isArray(t.rows) && t.rows.length > 0) ||
          (t.text != null && t.text !== ""),
      );
    for (let i = 0; i < toShow.length; i++) {
      const { text: rawText, card = false, rows = null, title = null } = toShow[i];
      // Structured card (key/value rows) — shown immediately, no typing animation.
      if (Array.isArray(rows) && rows.length > 0) {
        setTyping(true);
        await sleep(PRE_TYPE_MS);
        const id = makeId();
        setMessages((prev) => [
          ...prev,
          { id, from: "bot", text: "", card: true, rows, title, highlight },
        ]);
        setTyping(false);
        if (i < toShow.length - 1) await sleep(BETWEEN_MS);
        continue;
      }
      setTyping(true);
      await sleep(PRE_TYPE_MS);
      const id = makeId();
      setMessages((prev) => [...prev, { id, from: "bot", text: "", highlight, card }]);
      setTyping(false);
      const full = String(rawText);
      for (let j = TYPE_CHARS_PER_TICK; j < full.length; j += TYPE_CHARS_PER_TICK) {
        await sleep(TYPE_TICK_MS);
        const partial = full.slice(0, j);
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: partial } : m)));
      }
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: full } : m)));
      if (i < toShow.length - 1) await sleep(BETWEEN_MS);
    }
  };

  const pushUserMessage = (text) => {
    if (!text) return;
    setMessages((prev) => [...prev, userMsg(text)]);
  };

  // ----- async helpers -----

  const loadStationData = useCallback(async (stationId) => {
    const subdomain = getSubdomain();
    setLoading(true);
    try {
      const [dining, options] = await Promise.all([
        fetchDiningOptions(stationId, subdomain).catch(() => []),
        fetchStationOptions(stationId, subdomain).catch(() => []),
      ]);
      const sortedDining = Array.isArray(dining)
        ? [...dining].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [];
      const sortedOptions = Array.isArray(options)
        ? options.map((a) => ({
            group: a.group,
            items: (a.items || []).sort((x, y) => (x.order ?? 0) - (y.order ?? 0)),
          }))
        : [];
      const newCtx = { ...contextData, diningOptions: sortedDining, stationOptions: sortedOptions };
      setContextData(newCtx);
      return newCtx;
    } finally {
      setLoading(false);
    }
  }, [contextData]);

  const subscribePush = useCallback(async () => {
    if (!vapidKeyRef.current && sendViaWS) {
      sendViaWS({ type: "get-vapid-key" });
      await new Promise((resolve, reject) => {
        const start = Date.now();
        const check = setInterval(() => {
          if (vapidKeyRef.current) {
            clearInterval(check);
            resolve();
          } else if (Date.now() - start > 5000) {
            clearInterval(check);
            reject(new Error("Push notifications are not ready yet."));
          }
        }, 100);
      });
    }
    if (!vapidKeyRef.current) {
      throw new Error("Push notifications are not ready yet.");
    }
    const perm = await requestPermission();
    if (perm !== "granted") {
      throw new Error("Push permission was denied.");
    }
    await subscribe(vapidKeyRef.current);
    const subRes = await getSubscription();
    const sub = subRes?.toJSON();
    if (!sub) throw new Error("Couldn't get push subscription.");
    return {
      type: "push",
      auth: sub.keys.auth,
      p256dh: sub.keys.p256dh,
      endpoint: sub.endpoint,
    };
  }, [sendViaWS]);

  // ----- core advance logic -----

  const advance = useCallback(
    async (state, rt, ctxOverride, nextStepId) => {
      const ctx = buildCtx(ctxOverride);
      setStepId(nextStepId);
      setSelectedChips([]);
      setInputError(null);

      const view = getStepView(nextStepId, ctx, state, rt);
      await pushBotMessages(view.messages);

      if (nextStepId === "welcome" && view.autoAdvance) {
        const after = pickNextStep(ctx, state, rt);
        return advance(state, rt, ctxOverride, after);
      }

      if (nextStepId === "pick_station" && view.autoAdvance) {
        const { state: nState, runtime: nRt } = applyChipAction(nextStepId, view.autoValue, ctx, state, rt);
        setOrderState(nState);
        setRuntime(nRt);
        const loadedCtx = await loadStationData(view.autoValue);
        const fullCtx = { ...loadedCtx, stations: stationsRef.current };
        const after = pickNextStep(fullCtx, nState, nRt);
        return advance(nState, nRt, loadedCtx, after);
      }

      if (nextStepId === "pick_dining" && view.autoAdvance) {
        const { state: nState, runtime: nRt } = applyChipAction(nextStepId, view.autoValue, ctx, state, rt);
        setOrderState(nState);
        setRuntime(nRt);
        const after = pickNextStep(ctx, nState, nRt);
        return advance(nState, nRt, ctxOverride, after);
      }

      if (nextStepId === "pick_menu_group" && view.autoAdvance) {
        if (view.autoValue !== undefined) {
          const { state: nState, runtime: nRt } = applyChipAction(nextStepId, view.autoValue, ctx, state, rt);
          setOrderState(nState);
          setRuntime(nRt);
          const after = pickNextStep(ctx, nState, nRt);
          return advance(nState, nRt, ctxOverride, after);
        }
        const nrt = { ...rt, menuGroupIndex: rt.menuGroupIndex + 1 };
        setRuntime(nrt);
        const after = pickNextStep(ctx, state, nrt);
        return advance(state, nrt, ctxOverride, after);
      }

      if (nextStepId === "subscribe_push") {
        try {
          const notif = await subscribePush();
          const nState = { ...state, notification: notif };
          const nRt = { ...rt, pushSubscribed: true };
          setOrderState(nState);
          setRuntime(nRt);
          const after = pickNextStep(ctx, nState, nRt);
          return advance(nState, nRt, ctxOverride, after);
        } catch (e) {
          const reason = e.message || "Couldn't enable push notifications.";
          pushBotMessages([phrases.pushFailRetry(reason)]);
          const nState = { ...state, notification: null };
          setOrderState(nState);
          return advance(nState, rt, ctxOverride, "ask_notification_type");
        }
      }

      if (nextStepId === "check_telegram") {
        const nState = { ...state, notification: { type: "telegram" } };
        const nRt = { ...rt, telegramChecked: true };
        setOrderState(nState);
        setRuntime(nRt);
        const after = pickNextStep(ctx, nState, nRt);
        return advance(nState, nRt, ctxOverride, after);
      }
    },
    [buildCtx, subscribePush, loadStationData],
  );

  // ----- bootstrap on open -----

  const advanceRef = useRef(null);
  useEffect(() => { advanceRef.current = advance; }, [advance]);

  useEffect(() => {
    if (!isOpen || bootstrapped) return;
    setBootstrapped(true);
    setLoading(true);
    const subdomain = getSubdomain();

    fetchCustomerNotifications(subdomain)
      .then((notifs) => {
        setNotificationOptions(Array.isArray(notifs) ? notifs : []);
      })
      .catch((e) => console.warn("[chat] notifications failed", e));

    fetchRepeatSuggestion(subdomain)
      .catch((e) => {
        console.warn("[chat] repeat-suggestion failed", e);
        return { suggestion: null };
      })
      .then(async (repeatRes) => {
        const suggestion = repeatRes?.suggestion || null;
        // Use the freshest customer (ref), falling back to the closure value —
        // the /me query may have resolved after this effect first ran.
        const freshCustomer = customerRef.current ?? customer ?? null;
        const prefilled = applyCustomerPrefill(EMPTY_STATE, freshCustomer);
        let diningOptions = null;
        let stationOptions = null;
        if (suggestion?.food_station_id) {
          try {
            const [dining, options] = await Promise.all([
              fetchDiningOptions(suggestion.food_station_id, subdomain).catch(() => []),
              fetchStationOptions(suggestion.food_station_id, subdomain).catch(() => []),
            ]);
            diningOptions = Array.isArray(dining)
              ? [...dining].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              : [];
            stationOptions = Array.isArray(options)
              ? options.map((a) => ({
                  group: a.group,
                  items: (a.items || []).sort((x, y) => (x.order ?? 0) - (y.order ?? 0)),
                }))
              : [];
          } catch (e) {
            console.warn("[chat] preload station data failed", e);
          }
        }
        const newCtx = { suggestion, diningOptions, stationOptions, customer: freshCustomer };
        setContextData(newCtx);
        setOrderState(prefilled);
        setRuntime(EMPTY_RUNTIME);
        setLoading(false);
        try {
          (advanceRef.current || advance)(prefilled, EMPTY_RUNTIME, newCtx, "welcome");
        } catch (e) {
          console.error("[chat] advance(welcome) threw", e);
        }
      })
      .catch((e) => {
        console.error("[chat] bootstrap rejected", e);
        setLoading(false);
        setMessages((m) => [...m, botMsg(phrases.bootstrapError())]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bootstrapped]);

  // ----- order submission -----

  const submitOrder = useCallback(
    async (state) => {
      setLoading(true);
      setStepId("submitting");
      pushBotMessages([phrases.placingOrder()]);
      try {
        const payload = buildOrderPayload(state, getSubdomain(), customerRef.current?.id);
        const order = await apiCreateOrder(payload);
        pushBotMessages(
          [
            order?.id
              ? phrases.orderPlacedWithId(order.id)
              : phrases.orderPlaced(),
          ],
          { highlight: true },
        );
        setStepId("done");
      } catch (e) {
        const msg =
          e?.body?.message ||
          e?.body?.errors?.[0]?.message ||
          e?.message ||
          "Failed to place the order.";
        pushBotMessages([msg, phrases.orderFailedSuffix()]);
        setStepId("summary");
      } finally {
        setLoading(false);
      }
    },
    [phrases],
  );

  // ----- restart -----

  const restart = useCallback(() => {
    const prefilled = applyCustomerPrefill(EMPTY_STATE, customerRef.current);
    setOrderState(prefilled);
    setRuntime(EMPTY_RUNTIME);
    setSelectedChips([]);
    setInputError(null);
    setMessages([]);
    advance(prefilled, EMPTY_RUNTIME, contextData, "welcome");
  }, [contextData, advance]);

  // ----- chip click -----

  const handleChipClick = useCallback(
    async (chip) => {
      if (loading || typing) return;
      setInputError(null);
      const ctx = buildCtx();
      const view = getStepView(stepId, ctx, orderState, runtime);

      if (chip.action === "restart") {
        pushUserMessage(chip.label);
        restart();
        return;
      }

      if (chip.action === "confirm") {
        pushUserMessage(chip.label);
        submitOrder(orderState);
        return;
      }

      if (chip.action === "toggle_allergens") {
        setRuntime((r) => ({ ...r, showAllergenItems: !r.showAllergenItems }));
        setSelectedChips([]);
        return;
      }

      if (view.multi && chip.value !== "__skip__" && chip.value !== "__done__") {
        setSelectedChips((prev) => {
          const exists = prev.includes(chip.value);
          if (exists) return prev.filter((v) => v !== chip.value);
          if (view.noneValue) {
            if (chip.value === view.noneValue) return [view.noneValue];
            if (prev.length === 1 && prev[0] === view.noneValue) return [chip.value];
          }
          if (view.multiMax && prev.length >= view.multiMax) return prev;
          return [...prev, chip.value];
        });
        return;
      }

      if (stepId === "pick_menu_group" && chip.value === "__done__" && selectedChips.length === 0) {
        setInputError("Please choose at least one item.");
        return;
      }

      let actionValue = chip.value;
      if (view.multi && chip.value === "__done__") {
        actionValue = stepId === "ask_allergies" && selectedChips.length === 0 ? ["none"] : selectedChips;
      }

      const userLabel =
        chip.value === "__done__"
          ? labelForChip(stepId, actionValue, ctx, runtime) || "Done"
          : chip.label || labelForChip(stepId, chip.value, ctx, runtime);
      pushUserMessage(userLabel);

      const { state: nextState, runtime: nextRuntime } = applyChipAction(stepId, actionValue, ctx, orderState, runtime);
      setOrderState(nextState);
      setRuntime(nextRuntime);
      setSelectedChips([]);

      if (stepId === "pick_station") {
        const loadedCtx = await loadStationData(actionValue);
        const fullCtx = { ...loadedCtx, stations: stationsRef.current };
        const after = pickNextStep(fullCtx, nextState, nextRuntime);
        return advance(nextState, nextRuntime, loadedCtx, after);
      }

      if (stepId === "welcome" && chip.value === "__repeat__" && nextState.food_station_id) {
        const loadedCtx = await loadStationData(nextState.food_station_id);
        const fullCtx = { ...loadedCtx, stations: stationsRef.current };

        const { disabledNames, removedCount } = classifyRepeatItems(
          fullCtx,
          nextState.options_id,
        );
        if (disabledNames.length + removedCount > 0) {
          const names = disabledNames.join(", ");
          await pushBotMessages([
            names
              ? `Some items from your last order aren't available anymore: ${names}.`
              : "Some items from your last order aren't available anymore.",
            "Let's choose different options instead.",
          ]);
          const freshState = { ...nextState, options_id: [] };
          const freshRuntime = {
            ...nextRuntime,
            flow: "new",
            menuGroupIndex: 0,
          };
          setOrderState(freshState);
          setRuntime(freshRuntime);
          const after = pickNextStep(fullCtx, freshState, freshRuntime);
          return advance(freshState, freshRuntime, loadedCtx, after);
        }

        const after = pickNextStep(fullCtx, nextState, nextRuntime);
        return advance(nextState, nextRuntime, loadedCtx, after);
      }

      if (stepId === "ask_allergies" && customerRef.current?.allergies == null) {
        patchCustomerAllergies(nextState.allergies).catch(() => {});
      }

      const after = pickNextStep(ctx, nextState, nextRuntime);
      advance(nextState, nextRuntime, undefined, after);
    },
    [loading, typing, stepId, orderState, runtime, selectedChips, buildCtx, restart, submitOrder, loadStationData, advance],
  );

  const handleVoiceMultiSelect = useCallback(
    (values) => {
      if (loading || typing) return;
      const ctx = buildCtx();
      const stepView = getStepView(stepId, ctx, orderState, runtime);
      if (!stepView.multi) return;

      if (stepId === "pick_menu_group" && values.length === 0) {
        setInputError("Please choose at least one item.");
        return;
      }

      const actionValue =
        stepId === "ask_allergies" && values.length === 0 ? ["none"] : values;

      const userLabel = labelForChip(stepId, actionValue, ctx, runtime) || "Done";
      pushUserMessage(userLabel);

      const { state: nextState, runtime: nextRuntime } = applyChipAction(
        stepId,
        actionValue,
        ctx,
        orderState,
        runtime,
      );
      setOrderState(nextState);
      setRuntime(nextRuntime);
      setSelectedChips([]);

      if (stepId === "ask_allergies" && customerRef.current?.allergies == null) {
        patchCustomerAllergies(nextState.allergies).catch(() => {});
      }

      const after = pickNextStep(ctx, nextState, nextRuntime);
      advance(nextState, nextRuntime, undefined, after);
    },
    [loading, typing, stepId, orderState, runtime, buildCtx, advance],
  );

  const enableVoiceMode = useCallback(() => {
    setRuntime((r) => (r.voiceMode ? r : { ...r, voiceMode: true }));
  }, []);

  const stopVoiceMode = useCallback(() => {
    setRuntime((r) => (r.voiceMode ? { ...r, voiceMode: false } : r));
  }, []);

  // ----- text submit -----

  const handleTextSubmit = useCallback(
    (text) => {
      if (loading || typing) return;
      const err = validateTextInput(stepId, text);
      if (err) {
        setInputError(err);
        return;
      }
      setInputError(null);
      const trimmed = (text || "").trim();

      const ctx = buildCtx();
      const { state: nextState, runtime: nextRuntime } = applyTextInput(stepId, trimmed, ctx, orderState, runtime);

      if (stepId === "ask_notification_phone") {
        pushUserMessage(`+1${trimmed.replace(/\D/g, "").slice(0, 10)}`);
      } else {
        pushUserMessage(trimmed || "(skipped)");
      }

      setOrderState(nextState);
      setRuntime(nextRuntime);

      const after = pickNextStep(ctx, nextState, nextRuntime);
      advance(nextState, nextRuntime, undefined, after);
    },
    [loading, typing, stepId, orderState, runtime, buildCtx, advance],
  );

  // ----- view derivation -----

  const ctxView = buildCtx();
  const view = getStepView(stepId, ctxView, orderState, runtime);

  const uiChips = (() => {
    const baseChips = view.chips || [];
    const hasItems = Array.isArray(view.items) && view.items.length > 0;
    const needsDone = view.multi && (baseChips.length > 0 || hasItems || stepId === "ask_allergies");
    if (!view.multi) return baseChips;
    return [
      ...baseChips.map((c) => ({ ...c, selected: selectedChips.includes(c.value) })),
      ...(needsDone
        ? [{
            label: selectedChips.length
              ? phrases.doneMultiLabel(selectedChips.length)
              : stepId === "ask_allergies"
                ? phrases.noAllergiesLabel()
                : phrases.doneMultiLabel(0),
            value: "__done__",
            action: "done",
            variant: "confirm",
          }]
        : []),
    ];
  })();

  const uiItems = (view.items || []).map((item) => ({
    ...item,
    selected: selectedChips.includes(item.value),
  }));

  return {
    messages,
    chips: uiChips,
    items: uiItems,
    input: view.input || null,
    multi: !!view.multi,
    multiMax: view.multiMax || null,
    loading,
    typing,
    waiting: !!view.waiting,
    inputError,
    stepId,
    isDone: stepId === "done",
    stationId: orderState.food_station_id,
    voiceMode: !!runtime.voiceMode,
    enableVoiceMode,
    stopVoiceMode,
    handleChipClick,
    handleVoiceMultiSelect,
    handleTextSubmit,
    restart,
  };
};

export default useChatBot;
